import { ApiSearchIcon } from './icons/ApiSearchIcon';
import BookIcon from '@material-ui/icons/Book';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import GroupIcon from '@material-ui/icons/Group';
import HomeIcon from '@material-ui/icons/Home';
import InfinityLogoIcon from './icons/InfinityLogoIcon';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { makeStyles } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import { PropsWithChildren } from 'react';
import SamLogoIcon from './icons/SamLogoIcon';
import ScoreIcon from '@material-ui/icons/Score';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useSetupDatadogRum } from './hooks/useSetupDatadogRum';

import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const infSuppTicketUrl = configApi.getString('app.support.infSuppTicketUrl');
  const scalarAssistantModuleUrl = configApi.getString(
    'scalarAssistantModule.url',
  );

  useSetupDatadogRum();
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="home" text="Home" />
          <SidebarItem icon={GroupIcon} to="teams" text="Teams" />
          <SidebarItem icon={BookIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={ScoreIcon} to="score-cards" text="Score Cards" />
          <SidebarItem icon={ExtensionIcon} to="apis" text="APIs" />
          <SidebarItem
            icon={ToggleOnIcon}
            to="feature-flags"
            text="Feature Flags"
          />
          <SidebarItem icon={SmartToyIcon} to="amp/mcp" text="Amp" />
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Templates"
          />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            <SidebarItem
              icon={ApiSearchIcon}
              text="AsyncAPIs"
              onClick={() => {
                window.open(
                  'https://framework.pages.transics-cicd.aws.zf.com/doc-asyncapi-internal/',
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            />
          </SidebarScrollWrapper>
          <SidebarScrollWrapper>
            <SidebarItem
              icon={TrendingUpIcon}
              text="NFR quality"
              onClick={() =>
                window.open(
                  'https://infinity-project-quality-dashboard-8e64a9.pages.transics-cicd.aws.zf.com',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            />
          </SidebarScrollWrapper>
          <SidebarScrollWrapper>
            <SidebarItem
              icon={SamLogoIcon}
              text="SAM"
              onClick={() =>
                window.open(
                  scalarAssistantModuleUrl,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            />
          </SidebarScrollWrapper>
          {/* Disable Feedback Sidebar - DYNAMITE-1454 */}
          {/* <SidebarScrollWrapper>
            <SidebarItem icon={TextsmsOutlined} to="feedback" text="Feedback" />
          </SidebarScrollWrapper> */}
          <SidebarScrollWrapper>
            <SidebarItem
              icon={InfinityLogoIcon}
              text="Support"
              onClick={() =>
                window.open(infSuppTicketUrl, '_blank', 'noopener,noreferrer')
              }
            />
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
